import Typography from '@mui/material/Typography'
import { Box, Card, CardMedia } from '@mui/material'

import { sort } from 'ramda'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectCourseById,
  selectCompletedClasses,
  selectClassesByCourseID,
  selectClassesStatus,
  fetchCompletedClasses,
} from 'src/v3/self-paced/self-paced.slice'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactElement, useEffect, useState } from 'react'
import noop from 'src/utilities/noop'
import { fetchCourseVersion } from 'src/v3/classroom-v2/utils/api'
import { SelfPacedCoursePageSkeleton } from 'src/v3/self-paced/components/skeletons'
import {
  coursePublicImageSrc,
  onCourseImageError,
} from 'src/v3/self-paced/utils/course-thumbnail'

let diff = (a: any, b: any) => a.sequence - b.sequence

export default (): ReactElement => {
  let { courseId } = useParams()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let course = useAppSelector(selectCourseById(courseId ?? ''))
  let classes = useAppSelector(selectClassesByCourseID(courseId ?? ''))
  let classesStatus = useAppSelector(selectClassesStatus)
  let completedClasses = useAppSelector(selectCompletedClasses)

  let [publishedVersion, setPublishedVersion] = useState<number | undefined>()

  useEffect(() => {
    dispatch(fetchCompletedClasses())
  }, [])

  useEffect(() => {
    if (!courseId) return
    fetchCourseVersion(courseId)
      .then((r) => setPublishedVersion(r.publishedVersion ?? 1))
      .catch(() => setPublishedVersion(1))
  }, [courseId])

  useEffect(() => {
    if (!courseId || publishedVersion === undefined) return
    if (classes.length > 1) return
    // v2 courses that were never migrated from v1 have no class records at
    // all, so a 0-class course only redirects once we know it's v2 —
    // otherwise there's no legacy classroom target to send it to.
    if (classes.length === 0 && publishedVersion !== 2) return
    if (publishedVersion === 2) {
      navigate(`/classroom-v2/${courseId}`, { replace: true })
    } else {
      navigate(`/classroom/${courseId}/${classes[0].id}`, { replace: true })
    }
  }, [classes, courseId, publishedVersion, navigate])

  // Single-class courses always redirect away (to Classroom v2 or legacy
  // classroom) once the version check resolves, so this page's own markup
  // would otherwise flash briefly before that navigation fires. Zero-class
  // courses redirect too, but only to Classroom v2 (v1 has no such thing as
  // a classless course). Until classes and the version check have both
  // resolved we don't yet know which case applies, so hold off rendering
  // the real page to avoid a flash in either case. Multi-class courses
  // never redirect, so once classes have loaded and there's more than one,
  // it's safe to show the grid immediately rather than waiting on the
  // version check.
  let classesLoaded = classesStatus === 'fulfilled'
  let willRedirect =
    !classesLoaded ||
    classes.length === 1 ||
    (classes.length === 0 && publishedVersion !== 1)

  if (willRedirect) {
    return <SelfPacedCoursePageSkeleton />
  }

  return (
    <>
      <Box sx={{ px: 3, py: 4 }}>
        <Typography variant="h1" component="h1">
          {course?.name}
        </Typography>
      </Box>
      <Box className="mt-4 px-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {sort(diff, classes).map((classItem: any) => {
            let isCompleted = completedClasses.some((c) => c === classItem.id)
            // TODO: This needs to reflect the index of the class - which is in the model yet
            let isNext = completedClasses.some(
              (c) =>
                c ===
                classes.find((x) => x.sequence === classItem.sequence - 1)?.id,
            )
            let isFirst =
              classes.find((c) => c.sequence === 0)?.id === classItem.id
            let enabled = isCompleted || isNext || isFirst

            return (
              <Box
                key={classItem.id}
                sx={{
                  cursor: enabled ? 'pointer' : 'not-allowed',
                  transition: 'transform 0.3s ease',
                  '&:hover': enabled
                    ? {
                        transform: 'scale(1.05)',
                      }
                    : {},
                  position: 'relative',
                }}
                onClick={
                  enabled && courseId && classItem?.id
                    ? () => {
                        if (publishedVersion === 2) {
                          navigate(`/classroom-v2/${courseId}`)
                        } else {
                          navigate(`/classroom/${courseId}/${classItem.id}`)
                        }
                      }
                    : noop
                }
              >
                <Card
                  sx={{
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    filter: enabled ? 'brightness(100%)' : 'brightness(30%)',
                    border: 'none',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <CardMedia
                    component="img"
                    image={
                      classItem?.imageName
                        ? coursePublicImageSrc(classItem.imageName)
                        : undefined
                    }
                    onError={
                      classItem?.imageName
                        ? (event) =>
                            onCourseImageError(event, classItem.imageName)
                        : undefined
                    }
                    alt={classItem.name}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      aspectRatio: '16/9',
                      objectFit: 'cover',
                    }}
                  />
                </Card>
              </Box>
            )
          })}
        </div>
      </Box>
    </>
  )
}
